import {
  TrendingUp,
  Car,
  Users,
  Briefcase,
  Building2,
  Fuel,
  TriangleAlert,
  Wrench,
  Clock,
  Send,
  MapPin,
  History,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Route,
  type LucideIcon,
} from 'lucide-react';
import { UserRole } from '../../types/UserRole';

export interface SidebarItem {
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

export interface SidebarGroup {
  label: string;
  items: SidebarItem[];
  visibleForRole?: UserRole[];
}

const ADMIN_SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    label: 'Gestão',
    items: [
      { path: '/admin/dashboard', label: 'Dashboard', icon: TrendingUp, end: true },
      { path: '/admin/veiculos', label: 'Veículos', icon: Car },
      { path: '/admin/funcionarios', label: 'Funcionários', icon: Users },
      { path: '/admin/provedores', label: 'Fornecedores', icon: Briefcase },
      { path: '/admin/departamento', label: 'Departamento', icon: Building2 },
    ],
  },
  {
    label: 'Operações',
    items: [
      { path: '/admin/abastecimento', label: 'Abastecimento', icon: Fuel },
      { path: '/admin/incidentes', label: 'Incidentes', icon: TriangleAlert },
      { path: '/admin/manutencao', label: 'Manutenção', icon: Wrench },
      { path: '/admin/check-in', label: 'Check-In', icon: Clock },
      { path: '/admin/solicitacao-viagem', label: 'Solicitação de Viagem', icon: Send },
      { path: '/admin/rastreio', label: 'Rastreamento', icon: MapPin },
    ],
  },
  {
    label: 'Históricos',
    items: [
      { path: '/admin/historico-viagens', label: 'Histórico de Viagens', icon: History },
      { path: '/admin/historico-manutencao', label: 'Histórico de Manutenção', icon: Wrench },
      { path: '/admin/historico-incidentes', label: 'Histórico de Incidentes', icon: TriangleAlert },
      { path: '/admin/historico-abastecimento', label: 'Histórico de Abastecimento', icon: Fuel },
      { path: '/admin/historico-check-in', label: 'Histórico de Check-In', icon: Clock },
    ],
  },
  {
    label: 'Relatórios',
    visibleForRole: [UserRole.ADMIN],
    items: [
      { path: '/admin/relatorios', label: 'Relatórios', icon: BarChart3 },
    ],
  },
];

const CLIENT_SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    label: 'Gestão',
    items: [
      { path: '/client/dashboard', label: 'Dashboard', icon: TrendingUp, end: true },
      { path: '/client/veiculos', label: 'Veículos', icon: Car },
      { path: '/client/funcionarios', label: 'Funcionários', icon: Users },
    ],
  },
  {
    label: 'Operações',
    items: [
      { path: '/client/abastecimento', label: 'Abastecimento', icon: Fuel },
      { path: '/client/incidentes', label: 'Incidentes', icon: TriangleAlert },
      { path: '/client/manutencao', label: 'Manutenção', icon: Wrench },
      { path: '/client/check-in', label: 'Check-In', icon: Clock },
      { path: '/client/solicitacao-viagem', label: 'Solicitação de Viagem', icon: Send },
    ],
  },
  {
    label: 'Históricos',
    items: [
      { path: '/client/historico-viagens', label: 'Histórico de Viagens', icon: History },
      { path: '/client/historico-incidentes', label: 'Histórico de Incidentes', icon: TriangleAlert },
      { path: '/client/historico-abastecimento', label: 'Histórico de Abastecimento', icon: Fuel },
      { path: '/client/historico-check-in', label: 'Histórico de Check-In', icon: Clock },
    ],
  },
  {
    label: 'Relatórios',
    items: [
      { path: '/client/relatorios', label: 'Relatórios', icon: BarChart3 },
    ],
  },
];

const USER_SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    label: 'Operações',
    items: [
      { path: '/user/check-in', label: 'Check-In', icon: CheckCircle2 },
      { path: '/user/veiculos', label: 'Veículos', icon: Car },
      { path: '/user/abastecimento', label: 'Abastecimento', icon: Fuel },
      { path: '/user/registro-incidentes', label: 'Registro de Incidentes', icon: TriangleAlert },
      { path: '/user/relatorio-pos-uso', label: 'Relatório pós-uso', icon: ClipboardCheck },
      { path: '/user/manutencao', label: 'Manutenção', icon: Wrench },
    ],
  },
  {
    label: 'Viagens',
    items: [
      { path: '/user/solicitacao-viagem', label: 'Solicitação de Viagem', icon: Route },
      { path: '/user/historico-viagens', label: 'Histórico de Viagens', icon: Clock },
    ],
  },
];

export function getSidebarGroupsForRole(role: UserRole): SidebarGroup[] {
  switch (role) {
    case UserRole.ADMIN:
      return ADMIN_SIDEBAR_GROUPS;
    case UserRole.CLIENT:
      return CLIENT_SIDEBAR_GROUPS;
    case UserRole.USER:
      return USER_SIDEBAR_GROUPS;
    default:
      return [];
  }
}

export const ADMIN_PATH_TITLE_MAP: Record<string, string> = {
  '/admin/dashboard': 'Painel Administrativo',
  '/admin/veiculos': 'Cadastro de Veículos',
  '/admin/funcionarios': 'Cadastro de Funcionários',
  '/admin/provedores': 'Cadastro de Provedores',
  '/admin/departamento': 'Cadastro de Departamentos',
  '/admin/abastecimento': 'Abastecimentos',
  '/admin/check-in': 'Check-In',
  '/admin/solicitacao-viagem': 'Solicitações de Viagem',
  '/admin/incidentes': 'Incidentes',
  '/admin/manutencao': 'Manutenções',
  '/admin/historico-viagens': 'Histórico de Viagens',
  '/admin/historico-incidentes': 'Histórico de Incidentes',
  '/admin/historico-abastecimento': 'Histórico de Abastecimento',
  '/admin/historico-check-in': 'Histórico de Check-In',
  '/admin/historico-manutencao': 'Histórico de Manutenções',
  '/admin/rastreio': 'Rastreio',
  '/admin/relatorios': 'Relatórios',
};

export const CLIENT_PATH_TITLE_MAP: Record<string, string> = {
  '/client/dashboard': 'Painel do Cliente',
  '/client/veiculos': 'Frota do setor',
  '/client/funcionarios': 'Equipe do setor',
  '/client/abastecimento': 'Abastecimentos',
  '/client/incidentes': 'Incidentes',
  '/client/manutencao': 'Manutenções',
  '/client/check-in': 'Check-In',
  '/client/solicitacao-viagem': 'Solicitação de Viagem',
  '/client/historico-viagens': 'Histórico de Viagens',
  '/client/historico-incidentes': 'Histórico de Incidentes',
  '/client/historico-abastecimento': 'Histórico de Abastecimento',
  '/client/historico-check-in': 'Histórico de Check-In',
  '/client/relatorios': 'Relatórios',
};
